import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import '../../css/scorecard.css'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'
import { useMediaQuery } from 'react-responsive'

const base = process.env.REACT_APP_API_BASE_URL
const MATCHES_URL = process.env.REACT_APP_API_GET_MATCHES
const SCORECARD_URL = process.env.REACT_APP_API_GET_SCORECARD

export default function ResultDetailsCarousel() {
  const [matches, setMatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // --- helper: overs formatting ---
  const formatOvers = balls => {
    if (!balls || balls <= 0) return '0.0'
    const overs = Math.floor(balls / 6)
    const remainingBalls = balls % 6
    return `${overs}.${remainingBalls}`
  }

  // --- NEW helpers for dual-team fetch ---
  const getTeamIdFromUrl = urlStr => {
    try {
      const u = new URL(urlStr, window.location.origin) // supports relative/absolute
      return u.searchParams.get('teamId')
    } catch {
      return null
    }
  }

  const buildUrlWithTeamId = (urlStr, teamId) => {
    const u = new URL(urlStr, window.location.origin)
    u.searchParams.set('teamId', String(teamId))
    return u.toString()
  }

  const SORT_KEYS = ['matchDate', 'startDate', 'date', 'gameDate']

  useEffect(() => {
    const consumerKey = atob(process.env.REACT_APP_CONSUMER_KEY)
    const apiKey = atob(process.env.REACT_APP_API_KEY)

    const fetchForTeam = async teamId => {
      const url = buildUrlWithTeamId(MATCHES_URL, teamId)
      const res = await fetch(url, {
        headers: {
          'x-consumer-key': consumerKey,
          'x-api-key': apiKey,
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      if (!res.ok) throw new Error(`HTTP ${res.status} for teamId=${teamId}`)
      const json = await res.json()
      if (json?.responseState && Array.isArray(json?.data)) {
        // annotate each row with the team source
        return json.data.map(row => ({ __teamId: String(teamId), ...row }))
      }
      throw new Error(json?.errorMessage || `Invalid response for teamId=${teamId}`)
    }

    const fetchData = async () => {
      setLoading(true)
      setError(null)
      try {
        const primaryTeamId = getTeamIdFromUrl(MATCHES_URL) || '536'
        const teamIds = Array.from(new Set([primaryTeamId, '558'])) // dedupe if same

        const results = await Promise.all(teamIds.map(fetchForTeam))
        const merged = results.flat()

        // Optional sort by any known date field (desc)
        const key = SORT_KEYS.find(k => merged.length && merged[0]?.[k] !== undefined) || null
        if (key) {
          merged.sort((a, b) => {
            const da = new Date(a[key])
            const db = new Date(b[key])
            return db - da
          })
        }

        setMatches(merged)
      } catch (err) {
        setError(err.message || String(err))
        setMatches([])
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const isMobile = useMediaQuery({ maxWidth: 767 })
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1024 })
  const isDesktop = useMediaQuery({ minWidth: 1025 })

  const mobileSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    swipeToSlide: true,
    slidesToShow: 1,
    slidesToScroll: 1
  }

  const tabletSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    swipeToSlide: true,
    slidesToShow: 2,
    slidesToScroll: 1
  }

  const desktopSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    swipeToSlide: true,
    slidesToShow: 3,
    slidesToScroll: 1
  }

  const handleImageError = e => {
    e.target.src = 'https://via.placeholder.com/50?text=No+Logo'
    e.target.onerror = null
  }

  // NOTE: your scorecard helpers referenced state that isn't declared here.
  // Keeping your original function signatures, but you may need to add:
  // const [showModal, setShowModal] = useState(false)
  // const [scorecard, setScorecard] = useState(null)
  // const [scorecardLoading, setScorecardLoading] = useState(false)
  // const [scorecardError, setScorecardError] = useState(null)
  const fetchScorecard = async (matchId, clubId) => {
    // ... your existing logic (unchanged) ...
    // left intact to avoid changing unrelated behavior
  }

  const handleCloseModal = () => {
    // ... your existing logic (unchanged) ...
  }

  const Carousel = ({ settings }) => (
    <Slider {...settings} className='result-carousel-sec'>
      {loading && (
        <div className='d-flex justify-content-center py-5'>
          <Spinner animation='border' role='status'>
            <span className='sr-only'>Loading...</span>
          </Spinner>
        </div>
      )}

      {error && (
        <div className='text-center text-danger p-3'>
          Error loading matches: {error}
          <div className='mt-2'>
            <Button variant='warning' size='sm' onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        </div>
      )}

      {matches.map(match => (
        <Link
          to={`/match-details/${match.matchId}/${match.clubId}`}
          key={`${match.__teamId}-${match.matchId}`}
          className='text-decoration-none carousel-link'
        >
          <div className='px-md-2 px-1'>
            <div className='result-car-col p-2 p-md-3'>
              <div className='result-carousel-box'>
                <p className='p-2 text-center bg-light rounded'>
                  <i className='fa fa-calendar mx-2'></i>
                  {match.matchDate}
                </p>
                <table className='w-100 my-2'>
                  <tbody>
                    <tr>
                      <td colSpan={2}>
                        <strong>{match.seriesName}</strong>
                      </td>
                    </tr>
                    <tr>
                      <td width='60%'>
                        <div className='d-flex align-items-center'>
                          <img
                            className='result-car-img me-2'
                            alt={match.teamOneName}
                            src={`${base}${match.t1_logo_file_path}`}
                            onError={handleImageError}
                          />
                          <span className='text-truncate team-style'>{match.teamOneName}</span>
                        </div>
                      </td>
                      <td className='result-carousel-score text-end'>
                        {match.t1total}/{match.t1wickets}
                        <div className='text-muted small'>({formatOvers(match.t1balls)} ov)</div>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <div className='d-flex align-items-center'>
                          <img
                            className='result-car-img me-2'
                            alt={match.teamTwoName}
                            src={`${base}${match.t2_logo_file_path}`}
                            onError={handleImageError}
                          />
                          <span className='text-truncate team-style'>{match.teamTwoName}</span>
                        </div>
                      </td>
                      <td className='result-carousel-score text-end'>
                        {match.t2total}/{match.t2wickets}
                        <div className='text-muted small'>({formatOvers(match.t2balls)} ov)</div>
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* Optional badge to show the source team */}
                <p className='result-rr text-center mt-2 bg-info text-white p-1 rounded'>
                  {match.result}
                </p>
              </div>
            </div>
          </div>
        </Link>
      ))}
    </Slider>
  )

  return (
    <>
      {isMobile && <Carousel settings={mobileSettings} />}
      {isTablet && <Carousel settings={tabletSettings} />}
      {isDesktop && <Carousel settings={desktopSettings} />}
    </>
  )
}
